
import React from 'react';
import { useApp } from '../store';
import { Link, useNavigate } from 'react-router-dom';
import { RequestStatus, UserRole } from '../types';
import { Briefcase, ChevronRight, Clock, MapPin, ChevronLeft, Hammer, Ruler } from 'lucide-react';

const ActiveJobs: React.FC = () => {
  const { requests, currentUser } = useApp();
  const navigate = useNavigate();

  const ongoingStatuses = [
    RequestStatus.LOCKED,
    RequestStatus.EN_ROUTE,
    RequestStatus.ARRIVED,
    RequestStatus.CLARIFYING
  ];

  const activeJobs = requests.filter(r => {
    if (currentUser?.role === UserRole.REQUESTER) {
      return r.requesterId === currentUser.id && ongoingStatuses.includes(r.status);
    } else {
      return r.assignedArtisanId === currentUser?.id && ongoingStatuses.includes(r.status);
    }
  });

  return (
    <div className="space-y-8 px-4 max-w-xl mx-auto reveal">
      <div className="flex items-center gap-5">
        <button 
          onClick={() => navigate(-1)} 
          className="p-3 bg-white border border-stone-200 hover:bg-stone-50 rounded-xl text-stone-600 transition-all shadow-sm"
        >
          <ChevronLeft size={24} />
        </button>
        <div>
          <h2 className="text-4xl font-black text-[#2d1b0f] tracking-tight font-serif">The Ledger</h2>
          <p className="text-stone-400 text-[10px] font-black uppercase tracking-[0.2em]">Active projects in progress</p>
        </div>
      </div>

      {activeJobs.length === 0 ? (
        <div className="bg-white border-4 border-dashed border-stone-100 rounded-[3rem] p-20 text-center shadow-inner group">
          <div className="w-20 h-20 bg-[#faf7f2] rounded-3xl flex items-center justify-center mx-auto mb-8 text-stone-200 group-hover:rotate-12 group-hover:text-amber-700 transition-all">
            <Ruler size={40} />
          </div>
          <h3 className="text-2xl font-black text-stone-800 mb-3 font-serif italic">No Active Commisions</h3>
          <p className="text-stone-400 mb-10 max-w-xs mx-auto font-medium leading-relaxed">
            {currentUser?.role === UserRole.REQUESTER 
              ? "Your workshop is currently quiet. Post a new need to find a master artisan."
              : "No current contracts are locked. Browse the nearby feed to find your next masterpiece."}
          </p>
          <Link 
            to={currentUser?.role === UserRole.REQUESTER ? "/create" : "/artisan"} 
            className="inline-block bg-[#3d2b1f] text-amber-50 px-10 py-5 rounded-2xl font-black text-xs uppercase tracking-[0.3em] shadow-2xl hover:bg-stone-900 transition-all border-b-8 border-black/30 active:border-b-0 active:translate-y-2"
          >
            {currentUser?.role === UserRole.REQUESTER ? "Open a Project" : "Enter the Feed"}
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6">
          {activeJobs.map(job => (
            <Link 
              key={job.id} 
              to={`/request/${job.id}`}
              className="group bg-[#2d1b0f] p-8 rounded-[2.5rem] border border-amber-900/30 shadow-2xl transition-all hover:scale-[1.02] block relative overflow-hidden"
            >
              <div className="absolute top-0 right-0 w-32 h-32 wood-grain opacity-10 pointer-events-none"></div>
              
              <div className="flex justify-between items-center mb-6 relative z-10">
                <span className="text-[10px] font-black uppercase tracking-[0.3em] text-amber-400 bg-amber-500/10 px-4 py-1.5 rounded-full border border-amber-500/20">
                  {job.category}
                </span>
                <span className="text-[10px] font-black uppercase tracking-[0.3em] text-amber-200/40 bg-white/5 px-3 py-1.5 rounded-lg border border-white/5">
                  {job.status}
                </span>
              </div>
              
              <h3 className="text-2xl font-black text-white mb-3 pr-10 relative font-serif italic">
                {currentUser?.role === UserRole.REQUESTER ? `Working with Artisan` : job.requesterName}
                <ChevronRight className="absolute right-0 top-1/2 -translate-y-1/2 text-white/10 group-hover:text-amber-400 group-hover:translate-x-2 transition-all" size={32} />
              </h3>

              <p className="text-amber-100/40 text-sm line-clamp-2 mb-8 italic font-medium">"{job.text}"</p>

              <div className="flex items-center gap-6 text-[9px] font-black uppercase tracking-[0.4em] text-stone-500 border-t border-white/5 pt-6 relative z-10">
                <div className="flex items-center gap-2"><Clock size={16} className="text-amber-600" /> {job.urgency}</div>
                <div className="ml-auto flex items-center gap-2"><MapPin size={16} className="text-rose-700" /> {job.location?.address.split(',')[0]}</div>
              </div>
              
              <div className="absolute bottom-0 left-0 w-full h-1 bg-amber-600 shadow-[0_0_15px_rgba(217,119,6,0.6)] animate-pulse"></div>
            </Link>
          ))}
        </div>
      )}
      
      <div className="pt-12 text-center opacity-10">
        <Hammer className="mx-auto text-stone-900" size={32} />
      </div>
    </div>
  );
};

export default ActiveJobs;
